<script setup lang="ts">
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
  type Placement,
  type Strategy,
} from "@floating-ui/vue";
import { ref } from "vue";

interface FloatingTooltipProps {
  text?: string;
  placement?: Placement;
  enabled?: boolean;
  strategy?: Strategy;
}
const {
  placement = "bottom",
  enabled = true,
  strategy = "fixed",
} = defineProps<FloatingTooltipProps>();

const isHover = ref(false);
const reference = ref<HTMLElement>();
const floating = ref<HTMLDivElement>();
const { floatingStyles } = useFloating(reference, floating, {
  placement,
  strategy,
  middleware: [
    offset(10),
    shift(),
    flip({ crossAxis: false, fallbackAxisSideDirection: placement === "bottom" ? "end" : "start" }),
    size({
      apply({ availableWidth, availableHeight, elements }) {
        const { width, height } = elements.floating.getBoundingClientRect();
        const styles: { [key: string]: string } = {};
        if (width > availableWidth) {
          styles["maxWidth"] = `${Math.max(0, availableWidth)}px`;
        }
        if (height > availableHeight) {
          styles["maxHeight"] = `${Math.max(0, availableHeight)}px`;
        }
        Object.assign(elements.floating.style, styles);
      },
    }),
  ],
  whileElementsMounted: autoUpdate,
});
</script>

<template>
  <span
    class="floating-tooltip"
    ref="reference"
    @mouseenter="isHover = true"
    @mouseleave="isHover = false"
  >
    <slot></slot>
    <Teleport to="body">
      <Transition name="fade">
        <span
          v-if="isHover && enabled"
          ref="floating"
          class="floating-tooltip-content shadow-md"
          :class="placement"
          :style="floatingStyles"
        >
          <template v-if="$slots.tooltipContent">
            <slot name="tooltipContent"></slot>
          </template>
          <template v-else>
            {{ text }}
          </template>
        </span>
      </Transition>
    </Teleport>
  </span>
</template>

<style scoped>
.floating-tooltip-content {
  position: fixed;
  z-index: 9000;
  width: max-content;
  max-width: 80dvw;
  padding: var(--numbers-6);
  border-radius: var(--numbers-4);
  background-color: var(--background-inverse);
  color: var(--content-primary-inverse);
}
</style>
